<script setup lang="ts">
import {computed, onBeforeUnmount, ref, watch} from 'vue'
import {Track} from 'livekit-client'
import VoiceAvatar from '~/components/voice-ui/VoiceAvatar/VoiceAvatar.vue'
import ParticipantContextMenu from '~/components/voice-ui/ParticipantContextMenu.vue'
import {Badge} from '@/components/ui/badge'
import {Card} from '@/components/ui/card'
import {
  ContextMenu,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import {Tooltip, TooltipContent, TooltipTrigger} from '@/components/ui/tooltip'
import {Camera, Eye, EyeOff, Hand, Maximize2, MicOff, MonitorUp, Signal, SignalHigh, SignalLow, SignalZero} from 'lucide-vue-next'
import {ConnectionQuality} from 'livekit-client'
import {useGroupsStore, type Participant} from '~/stores/groups'
import {resolveMediaUrl} from '~/lib/mediaCache'
import {usePlatform} from "~/composables/usePlatform.ts";

const props = defineProps<{
  participant: Participant
  /** `video` = a track is published (camera or screen-share, see `source`); `avatar` = nothing published. */
  kind: 'video' | 'avatar'
  /** Required when `kind === 'video'`: which track this tile's video normally shows. */
  source?: Track.Source
  /** Screen tiles only: when the participant also has their camera on, swap which stream is "main". */
  swapped?: boolean
}>()

// LiveKit participant identity is set server-side to the backend user id — look up that user's
// avatar URL from the group member cache (same source message/member avatars read from) and
// resolve it through the on-disk media cache, for local and remote participants alike.
const avatarUrl = computed(() => resolveMediaUrl(groupsStore.getUserAvatarUrl(props.participant.identity), 'avatars'))

const emit = defineEmits<{
  (e: 'registerVideo', el: HTMLVideoElement | null, sid: string, source: Track.Source): void
  (e: 'requestFullscreen', sid: string, source: Track.Source, name: string): void
  (e: 'toggleSwap'): void
  /** The main video's width / height, once known and whenever it changes (e.g. the phone rotates). */
  (e: 'aspect', ratio: number): void
}>()

function handleMainVideoResize(event: Event) {
  const video = event.target as HTMLVideoElement
  if (video.videoWidth && video.videoHeight) emit('aspect', video.videoWidth / video.videoHeight)
}

const initials = computed(() =>
    props.participant.name
        .split(' ')
        .filter(Boolean)
        .map(part => part[0])
        .join('')
        .toUpperCase()
        .slice(0, 2),
)

const groupsStore = useGroupsStore()

const isScreenTile = computed(() => props.kind === 'video' && props.source === Track.Source.ScreenShare)

const connectionQualityMeta = computed(() => {
  switch (props.participant.connectionQuality) {
    case ConnectionQuality.Excellent:
      return {icon: Signal, class: 'text-green-500', label: 'Отличное соединение'}
    case ConnectionQuality.Good:
      return {icon: SignalHigh, class: 'text-green-500', label: 'Хорошее соединение'}
    case ConnectionQuality.Poor:
      return {icon: SignalLow, class: 'text-yellow-500', label: 'Слабое соединение'}
    case ConnectionQuality.Lost:
      return {icon: SignalZero, class: 'text-destructive animate-pulse', label: 'Соединение потеряно'}
    default:
      return null
  }
})

// Volume/mute controls live in the shared `ParticipantContextMenu` (also used by the sidebar
// member list) — this only needs the mute state for the badge below, reactively (see
// `groupsStore.isParticipantMuted`) so it stays correct even when muted from the sidebar instead
// of this tile's own menu.
const isMutedForMe = computed(() => groupsStore.isParticipantMuted(props.participant.identity))

// A screen-sharer who also has their camera on gets a small corner tile of
// the other stream; `mainSource`/`pipSource` flip when the user clicks it.
// Someone else's screen share isn't subscribed to until "Смотреть стрим" is pressed (like Discord) —
// until then the tile shows that button instead of the stream: over their camera if it's on (the
// camera is the main video meanwhile, see `mainSource`), otherwise under their avatar. See
// `liveKitManager.setScreenShareWatched`. Your own share is a local track (nothing to download), so
// it always shows.
const isScreenShareWatched = computed(() => groupsStore.isScreenShareWatched(props.participant.identity))
const isStreamGated = computed(() => isScreenTile.value && !props.participant.is_local && !isScreenShareWatched.value)
function setWatching(watched: boolean) {
  groupsStore.setScreenShareWatched(props.participant.identity, watched)
}

const showPip = computed(() => isScreenTile.value && props.participant.isVideoEnabled && !isStreamGated.value)
const hasGatedCamera = computed(() => isStreamGated.value && !!props.participant.isVideoEnabled)
const mainSource = computed(() => {
  if (!isScreenTile.value) return props.source ?? Track.Source.Camera
  if (isStreamGated.value) return Track.Source.Camera
  return props.swapped ? Track.Source.Camera : Track.Source.ScreenShare
})
const pipSource = computed(() => (props.swapped ? Track.Source.ScreenShare : Track.Source.Camera))

// Camera feeds are mirrored (selfie-style) like every other video call app — on desktop only; phones
// show the camera as it is. Screen shares never are, since flipping shared text/UI would make them
// unreadable.
const isMainCameraMirrored = computed(() => mainSource.value === Track.Source.Camera)
const isPipCameraMirrored = computed(() => pipSource.value === Track.Source.Camera)

const mainVideoEl = ref<HTMLVideoElement | null>(null)
const pipVideoEl = ref<HTMLVideoElement | null>(null)

// IMPORTANT: swapping must re-point the *same* <video> element at a different
// track instead of destroying/recreating the element (e.g. via a `:key` tied
// to `swapped`) — recreating forces the browser to reinitialize the element
// and produces a black frame, and it's easy to accidentally clean up the
// wrong bucket in `videoElements` if the old/new source is read from a
// reactive value that already changed by the time the unmount ref fires.
// Doing the detach/attach explicitly here keeps it unambiguous.
watch(mainSource, (next, prev) => {
  emit('registerVideo', null, props.participant.sid, prev)
  if (mainVideoEl.value) emit('registerVideo', mainVideoEl.value, props.participant.sid, next)
})
watch(pipSource, (next, prev) => {
  emit('registerVideo', null, props.participant.sid, prev)
  if (pipVideoEl.value) emit('registerVideo', pipVideoEl.value, props.participant.sid, next)
})

function registerMainVideo(el: Element | null) {
  const next = el as HTMLVideoElement | null
  if (next === mainVideoEl.value) return
  mainVideoEl.value = next
  emit('registerVideo', mainVideoEl.value, props.participant.sid, mainSource.value)
}

function registerPipVideo(el: Element | null) {
  const next = el as HTMLVideoElement | null
  if (next === pipVideoEl.value) return
  pipVideoEl.value = next
  emit('registerVideo', pipVideoEl.value, props.participant.sid, pipSource.value)
}

onBeforeUnmount(() => {
  if (mainVideoEl.value) emit('registerVideo', null, props.participant.sid, mainSource.value)
  if (pipVideoEl.value) emit('registerVideo', null, props.participant.sid, pipSource.value)
})

// Desktop opens the full-screen viewer on double-click; phones on a single tap (bound in the
// template) — there's no hover there, and a double tap is easy to miss. Avatar-only tiles ignore it.
function requestFullscreen() {
  if (props.kind !== 'video' || isStreamGated.value) return
  const label = isScreenTile.value ? `${props.participant.name} (Экран)` : props.participant.name
  emit('requestFullscreen', props.participant.sid, mainSource.value, label)
}

const {isMobile} = usePlatform();

// A click anywhere on an unwatched stream's tile starts watching it (not just on the button); on
// phones a tap on a video tile otherwise opens it full screen.
function handleTileClick() {
  if (isStreamGated.value) {
    setWatching(true)
    return
  }
  if (isMobile.value) requestFullscreen()
}

</script>

<template>
  <ContextMenu>
    <ContextMenuTrigger as-child :disabled="participant.is_local">
      <Card
          class="relative group flex h-full w-full items-center shadow-none! justify-center overflow-hidden rounded-xl border-2 hover:border-primary/30 bg-primary-foreground/80 transition-colors duration-100 p-0 cursor-pointer"
          :class="[participant.isSpeaking ? 'border-green-500' : 'border-border']"
          :data-call-tile-sid="participant.sid"
          :data-call-tile-source="mainSource"
          @click.stop="handleTileClick"
          @dblclick.stop="requestFullscreen"
      >
        <video
            v-if="kind === 'video' && (!isStreamGated || hasGatedCamera)"
            :ref="registerMainVideo"
            class="h-full w-full object-contain"
            :class="isMainCameraMirrored && !isMobile ? '-scale-x-100' : ''"
            autoplay
            playsinline
            @loadedmetadata="handleMainVideoResize"
            @resize="handleMainVideoResize"
        />
        <VoiceAvatar
            v-else
            :class="isStreamGated ? 'size-12 shrink-0 sm:size-14' : 'size-16 shrink-0 sm:size-20'"
            fallback-class="text-lg font-semibold"
            :src="avatarUrl"
            :alt="participant.name"
            :fallback="initials"
        />

        <!-- Unwatched screen share: a "Смотреть стрим" button — at the bottom over their camera, or
             well below their avatar when the camera is off. The whole tile starts watching on click
             too (`handleTileClick`), the button just says so. -->
        <div
            v-if="isStreamGated"
            class="absolute inset-x-0 z-10 flex justify-center px-3"
            :class="hasGatedCamera ? 'bottom-8' : 'top-1/2 translate-y-12'"
        >
          <button
              type="button"
              class="flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-md transition-transform duration-150 hover:bg-primary/90 active:scale-95"
              @click.stop="setWatching(true)"
              @dblclick.stop
          >
            <Eye class="size-4"/>
            Смотреть стрим
          </button>
        </div>

        <!-- Camera-in-corner PiP for a participant who is both sharing their screen and on camera. Fixed position, not draggable. -->
        <button
            v-if="showPip"
            type="button"
            class="absolute bottom-2 left-2 z-20 h-16 w-24 overflow-hidden rounded-lg border border-white/20 bg-primary-foreground/80 shadow-lg transition-transform hover:scale-105 sm:h-20 sm:w-32"
            @click.stop="emit('toggleSwap')"
        >
          <video :ref="registerPipVideo" class="h-full w-full object-cover"
                 :class="isPipCameraMirrored && !isMobile ? '-scale-x-100' : ''" autoplay playsinline/>
        </button>

        <div
            v-if="kind === 'video' && !isStreamGated"
            class="absolute top-2 right-2 z-20 flex items-center gap-1 transition-opacity"
            :class="isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'"
        >
          <!-- Stops watching someone's stream (unsubscribes again) — back to the "Смотреть стрим" button. -->
          <button
              v-if="isScreenTile && !participant.is_local"
              type="button"
              aria-label="Перестать смотреть"
              class="rounded-full bg-black/40 p-1.5 text-white backdrop-blur-md hover:bg-black/60"
              @click.stop="setWatching(false)"
              @dblclick.stop
          >
            <EyeOff class="size-3.5"/>
          </button>
          <button
              v-if="!isMobile"
              type="button"
              aria-label="На весь экран"
              class="rounded-full bg-black/40 p-1.5 text-white backdrop-blur-md hover:bg-black/60"
              @click.stop="requestFullscreen"
          >
            <Maximize2 class="size-3.5"/>
          </button>
        </div>

        <div class="absolute bottom-2 right-2 z-20 flex items-center gap-1">
          <Tooltip v-if="connectionQualityMeta">
            <TooltipTrigger>
              <component :is="connectionQualityMeta.icon" class="size-4 drop-shadow"
                         :class="connectionQualityMeta.class"/>
            </TooltipTrigger>
            <TooltipContent>
              <p>{{ connectionQualityMeta.label }}</p>
            </TooltipContent>
          </Tooltip>
          <Badge v-if="participant.isHandRaised" class="rounded-full bg-yellow-500 p-1 shadow-sm hover:bg-yellow-500">
            <Hand class="size-3 text-white"/>
          </Badge>
          <Badge v-if="!isScreenTile && participant.isScreenShareEnabled"
                 class="rounded-full bg-blue-500 p-1 shadow-sm hover:bg-blue-500">
            <MonitorUp class="size-3 text-white"/>
          </Badge>
          <Badge v-if="kind === 'avatar' && participant.isVideoEnabled"
                 class="rounded-full bg-green-500 p-1 shadow-sm hover:bg-green-500">
            <Camera class="size-3 text-white"/>
          </Badge>
          <Tooltip v-if="!participant.isAudioEnabled">
            <TooltipTrigger>
              <Badge variant="destructive" class="rounded-full p-1 shadow-sm">
                <MicOff class="size-3"/>
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p>{{ participant.is_local ? 'Ваш микрофон выключен' : 'Микрофон выключен' }}</p>
            </TooltipContent>
          </Tooltip>
          <Tooltip v-if="isMutedForMe">
            <TooltipTrigger>
              <Badge class="rounded-full bg-muted-foreground/60 p-1 shadow-sm hover:bg-muted-foreground/60">
                <MicOff class="size-3 text-white"/>
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p>Заглушён вами</p>
            </TooltipContent>
          </Tooltip>
        </div>

        <div
            class="absolute top-2 left-2 z-10 max-w-[70%] truncate rounded-md border border-white/10 bg-black/60 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur-md">
          {{ participant.name }}{{ participant.is_local ? ' (Вы)' : '' }}{{ isScreenTile ? ' — экран' : '' }}
        </div>
      </Card>
    </ContextMenuTrigger>

    <ParticipantContextMenu
        v-if="!participant.is_local"
        :identity="participant.identity"
        :name="participant.name"
    />
  </ContextMenu>
</template>
